import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { MOCK_METRICS, MOCK_CHART_DATA, MOCK_TRANSACTIONS } from '../lib/mockData';
import { formatCurrency, formatDate } from '../lib/utils';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { ArrowUpRight, DollarSign, Users, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
export function Dashboard() {
  const [period, setPeriod] = useState('30d');
  return <div className="space-y-8" data-id="element-355">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-356">
        <div data-id="element-357">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-358">
            Dashboard
          </h2>
          <p className="text-slate-500 mt-1" data-id="element-359">
            Overview of your sales performance.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white p-1 rounded-lg border border-slate-200 shadow-sm" data-id="element-360">
          {['7d', '30d', '90d', '1y'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${period === p ? 'bg-[#218080] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-361">
              {p === '1y' ? 'Last Year' : `Last ${p.replace('d', ' Days')}`}
            </button>)}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-362">
        <Card data-id="element-363">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-364">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-365">
              Total Sales
            </CardTitle>
            <DollarSign className="h-4 w-4 text-[#218080]" data-id="element-366" />
          </CardHeader>
          <CardContent data-id="element-367">
            <div className="text-2xl font-bold" data-id="element-368">
              {formatCurrency(MOCK_METRICS.totalSales)}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-369">
              +20.1% from last month
            </p>
          </CardContent>
        </Card>
        <Card data-id="element-370">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-371">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-372">
              Unique Customers
            </CardTitle>
            <Users className="h-4 w-4 text-blue-600" data-id="element-373" />
          </CardHeader>
          <CardContent data-id="element-374">
            <div className="text-2xl font-bold" data-id="element-375">{MOCK_METRICS.uniqueUsers}</div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-376">+180 new this month</p>
          </CardContent>
        </Card>
        <Card data-id="element-377">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-378">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-379">
              Avg. Transaction
            </CardTitle>
            <CreditCard className="h-4 w-4 text-purple-600" data-id="element-380" />
          </CardHeader>
          <CardContent data-id="element-381">
            <div className="text-2xl font-bold" data-id="element-382">
              {formatCurrency(MOCK_METRICS.avgTransactionValue)}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-383">Per qualified sale</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7" data-id="element-384">
        {/* Sales Chart */}
        <Card className="col-span-4" data-id="element-385">
          <CardHeader data-id="element-386">
            <CardTitle data-id="element-387">Sales Over Time</CardTitle>
          </CardHeader>
          <CardContent className="pl-2" data-id="element-388">
            <div className="h-[300px]" data-id="element-389">
              <ResponsiveContainer width="100%" height="100%" data-id="element-390">
                <AreaChart data={MOCK_CHART_DATA} data-id="element-391">
                  <defs data-id="element-392">
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1" data-id="element-393">
                      <stop offset="5%" stopColor="#218080" stopOpacity={0.1} data-id="element-394" />
                      <stop offset="95%" stopColor="#218080" stopOpacity={0} data-id="element-395" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-396" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} data-id="element-397" />
                  <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `$${value}`} data-id="element-398" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} formatter={(value: number) => [formatCurrency(value), 'Sales']} data-id="element-399" />
                  <Area type="monotone" dataKey="sales" stroke="#218080" strokeWidth={2} fillOpacity={1} fill="url(#colorSales)" data-id="element-400" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Customer Cashback Chart */}
        <Card className="col-span-3" data-id="element-401">
          <CardHeader data-id="element-402">
            <CardTitle data-id="element-403">Customer Cashback</CardTitle>
          </CardHeader>
          <CardContent data-id="element-404">
            <div className="h-[300px]" data-id="element-405">
              <ResponsiveContainer width="100%" height="100%" data-id="element-406">
                <BarChart data={MOCK_CHART_DATA.slice(-7)} data-id="element-407">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-408" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} data-id="element-409" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }} formatter={(value: number) => formatCurrency(Math.abs(value))} data-id="element-410" />
                  <Bar name="Cashback to Customers" dataKey="cashback" fill="#93c5fd" radius={[4, 4, 0, 0]} data-id="element-411" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card data-id="element-412">
        <CardHeader className="flex flex-row items-center justify-between" data-id="element-413">
          <CardTitle data-id="element-414">Recent Transactions</CardTitle>
          <Button variant="outline" size="sm" data-id="element-415">
            Export CSV
          </Button>
        </CardHeader>
        <CardContent data-id="element-416">
          <div className="overflow-x-auto" data-id="element-417">
            <table className="w-full text-sm text-left" data-id="element-418">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-419">
                <tr data-id="element-420">
                  <th className="px-4 py-3" data-id="element-421">Date</th>
                  <th className="px-4 py-3" data-id="element-422">Transaction ID</th>
                  <th className="px-4 py-3" data-id="element-423">Amount</th>
                  <th className="px-4 py-3" data-id="element-424">Cashback</th>
                  <th className="px-4 py-3" data-id="element-425">Status</th>
                </tr>
              </thead>
              <tbody data-id="element-426">
                {MOCK_TRANSACTIONS.slice(0, 10).map(txn => <tr key={txn.id} className="border-b border-slate-100 hover:bg-slate-50" data-id="element-427">
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-428">
                      {formatDate(txn.date)}
                    </td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs" data-id="element-429">
                      {txn.id}
                    </td>
                    <td className="px-4 py-3 text-slate-900" data-id="element-430">
                      {formatCurrency(txn.amount)}
                    </td>
                    <td className="px-4 py-3 text-green-600 font-medium" data-id="element-431">
                      {formatCurrency(txn.cashback)}
                    </td>
                    <td className="px-4 py-3" data-id="element-432">
                      <Badge variant={txn.status === 'paid' ? 'success' : 'warning'} data-id="element-433">
                        {txn.status}
                      </Badge>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-center" data-id="element-434">
            <Button variant="ghost" size="sm" className="text-slate-500" data-id="element-435">
              View All Transactions
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>;
}